import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import router from '@/common/router';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { useInternational } from '@/common/locale';
import { useSimpleTextTooltip } from '@/common/components/molecules/simpleTextTooltip/simpleTextTooltip.uses';
import { BASE_MENU_VALUE } from '../baseMenu.define';
import type { MenuInfo } from '../types';

export const setup = () => {
  const { t } = useInternational();

  const baseMenuStore = useBaseMenuStore();
  const { subPanelInfo } = storeToRefs(baseMenuStore);
  const { closeSubPanel, selectCategory } = baseMenuStore;
  const tooltipMouseEvent = ref<MouseEvent | null>(null);

  const { onMouseEventInTarget } = useSimpleTextTooltip(tooltipMouseEvent);

  const panelMenuTitle = computed<string>(() => {
    if (!subPanelInfo.value.openedSubPanelMenuInfo) {
      return '';
    }
    if (subPanelInfo.value.openedSubPanelMenuInfo.value === BASE_MENU_VALUE.SETTINGS) {
      return t('WORD.GNB.SETTING');
    }
    return subPanelInfo.value.openedSubPanelMenuInfo.text;
  });

  const shouldMidMenuTitleRendered = (title: string) => {
    return title !== panelMenuTitle.value;
  };

  // 즐겨찾기 항목과 일반 항목을 분리
  const separatedMenuItems = computed<{
    favoriteItems: MenuInfo[];
    regularItems: MenuInfo[];
  }>(() => {
    if (!subPanelInfo.value.openedSubPanelMenuInfo?.subMenuList) {
      return { favoriteItems: [], regularItems: [] };
    }

    const favoriteItems: MenuInfo[] = [];
    const regularItems: MenuInfo[] = [];

    subPanelInfo.value.openedSubPanelMenuInfo.subMenuList.forEach((item) => {
      // 즐겨찾기 항목 확인 (favoriteId가 있거나 children 중에 favoriteId가 있는 경우)
      const hasFavorite = item.favoriteId || item.children?.some((child) => child.favoriteId);

      if (hasFavorite) {
        favoriteItems.push(item);
      } else {
        regularItems.push(item);
      }
    });

    return { favoriteItems, regularItems };
  });

  // 대시보드 메뉴의 즐겨찾기 항목과 일반 항목을 분리
  const separatedDashboardItems = computed<{
    favoriteItems: MenuInfo[];
    regularItems: MenuInfo[];
  }>(() => {
    if (!subPanelInfo.value.openedSubPanelMenuInfo?.subMenuList) {
      return { favoriteItems: [], regularItems: [] };
    }

    const favoriteItems: MenuInfo[] = [];
    const regularItems: MenuInfo[] = [];

    subPanelInfo.value.openedSubPanelMenuInfo.subMenuList.forEach((item) => {
      // 대시보드 항목의 경우 favoriteId로 즐겨찾기 여부 확인
      if (item.favoriteId) {
        favoriteItems.push(item);
      } else {
        regularItems.push(item);
      }
    });

    return { favoriteItems, regularItems };
  });

  const onClosePanel = () => {
    closeSubPanel();
  };

  const isEmptyDashboardItems = computed(() => {
    return (
      separatedDashboardItems.value.favoriteItems.length === 0 &&
      separatedDashboardItems.value.regularItems.length === 0
    );
  });

  const onClickGoToDashboardList = () => {
    const currentRoute = router.currentRoute.value;
    const isAlreadyOnDashboardList = currentRoute.name === 'dashboard_Dashboard List';

    if (isAlreadyOnDashboardList) {
      // 이미 대시보드 목록 화면이면 좌측 메뉴 선택만 변경하고 LNB 닫기
      selectCategory({ menuValue: BASE_MENU_VALUE.DASHBOARD });
      closeSubPanel();
    } else {
      router.push({ name: 'dashboard_Dashboard List' });
    }
  };

  return {
    t,

    panelMenuTitle,
    tooltipMouseEvent,
    subPanelInfo,

    shouldMidMenuTitleRendered,
    separatedMenuItems,
    separatedDashboardItems,
    isEmptyDashboardItems,

    onMouseEventInTarget,
    onClosePanel,
    onClickGoToDashboardList,
  };
};
